import { AgentApprovalMode, type AgentToolPolicy } from '@this+that/models';

import { toToolPolicyInputs } from './toolPolicyInput';
import {
  AgentSetupAssistantTurnMessage,
  AgentSetupConversationMessage,
  AgentSetupConversationState,
  AgentSetupDraftState,
  AgentSetupTrialDoneSummary,
  CreatedAgentDraft,
  EnterAgentSetupParams,
  HandlerSet,
} from './types';
import {
  type AgentOutboundDraft,
  EMPTY_OUTBOUND_DRAFT,
  outboundToDraft,
} from '../../components/AgentOutboundSettings/agentOutboundDraft';
import { AgentSetupPanelPhase } from '../../enums';
import {
  AgentSetupConversationRole,
  AgentSetupResultDelivery,
  AgentTemplateVisibility,
} from '../../services/agentTemplates.service';

function createInitialDraft({
  itemId,
  taskGroupId,
  suggestion,
}: EnterAgentSetupParams): AgentSetupDraftState {
  return {
    itemId,
    taskGroupId,
    templateId: null,
    name: '',
    job: '',
    toolSets: [],
    toolPolicies: [],
    visibility: AgentTemplateVisibility.PRIVATE,
    approvalMode: AgentApprovalMode.ASK,
    outbound: { ...EMPTY_OUTBOUND_DRAFT },
    exampleFromItem: suggestion?.exampleFromItem ?? null,
    connectionHints: suggestion?.connectionHints ?? [],
    resultDelivery: null,
    isSaving: false,
    isRegenerating: false,
    template: null,
    doneSummary: null,
    caveats: [],
    missingBrainReferences: [],
    suggestedDestinations: [],
  };
}

function createInitialConversation({
  suggestion,
}: EnterAgentSetupParams): AgentSetupConversationState {
  return {
    messages: [],
    isThinking: false,
    openingSuggestion: suggestion?.suggestedJob.trim() || null,
  };
}

export const handleEnterAgentSetup = (
  set: HandlerSet,
  params: EnterAgentSetupParams,
) => {
  set((state) => ({
    phaseByItemId: {
      ...state.phaseByItemId,
      [params.itemId]: AgentSetupPanelPhase.CONVERSATION,
    },
    draftByItemId: {
      ...state.draftByItemId,
      [params.itemId]: createInitialDraft(params),
    },
    conversationByItemId: {
      ...state.conversationByItemId,
      [params.itemId]: createInitialConversation(params),
    },
  }));
};

export const handleExitAgentSetup = (set: HandlerSet, itemId: string) => {
  set((state) => {
    const { [itemId]: _phase, ...remainingPhases } = state.phaseByItemId;
    const { [itemId]: _draft, ...remainingDrafts } = state.draftByItemId;
    const { [itemId]: _conversation, ...remainingConversations } =
      state.conversationByItemId;
    const finishedDraft =
      state.phaseByItemId[itemId] === AgentSetupPanelPhase.DONE
        ? state.draftByItemId[itemId]
        : undefined;
    return {
      phaseByItemId: remainingPhases,
      draftByItemId: remainingDrafts,
      conversationByItemId: remainingConversations,
      ...(finishedDraft
        ? {
            finishedByItemId: {
              ...state.finishedByItemId,
              [itemId]: finishedDraft,
            },
          }
        : {}),
    };
  });
};

export const handleReopenFinishedSetup = (set: HandlerSet, itemId: string) => {
  set((state) => {
    const { [itemId]: finishedDraft, ...remainingFinished } =
      state.finishedByItemId;
    if (!finishedDraft) {
      return {};
    }
    return {
      phaseByItemId: {
        ...state.phaseByItemId,
        [itemId]: AgentSetupPanelPhase.DONE,
      },
      draftByItemId: { ...state.draftByItemId, [itemId]: finishedDraft },
      finishedByItemId: remainingFinished,
    };
  });
};

export const handleGoToPhase = (
  set: HandlerSet,
  itemId: string,
  phase: AgentSetupPanelPhase,
) => {
  set((state) => ({
    phaseByItemId: { ...state.phaseByItemId, [itemId]: phase },
  }));
};

const updateDraft = (
  set: HandlerSet,
  itemId: string,
  updater: (draft: AgentSetupDraftState) => Partial<AgentSetupDraftState>,
) => {
  set((state) => {
    const current = state.draftByItemId[itemId];
    if (!current) {
      return {};
    }
    return {
      draftByItemId: {
        ...state.draftByItemId,
        [itemId]: { ...current, ...updater(current) },
      },
    };
  });
};

export const handleApplyCreatedDraft = (
  set: HandlerSet,
  itemId: string,
  draft: CreatedAgentDraft,
) => {
  updateDraft(set, itemId, () => ({
    templateId: draft.templateId,
    name: draft.name,
    job: draft.job ?? '',
    toolSets: draft.toolSets ?? [],
    toolPolicies: toToolPolicyInputs(draft.toolPolicies),
    approvalMode: draft.approvalMode ?? AgentApprovalMode.ASK,
    outbound: outboundToDraft(draft.outbound, draft.replyWait),
    exampleFromItem: draft.exampleFromItem ?? null,
    template: draft,
    caveats: [],
    missingBrainReferences: [],
    suggestedDestinations: [],
  }));
};

export const handleCompleteWithTemplate = (
  set: HandlerSet,
  itemId: string,
  template: { templateId: string; name: string },
) => {
  set((state) => {
    const current = state.draftByItemId[itemId];
    if (!current) {
      return {};
    }
    return {
      phaseByItemId: {
        ...state.phaseByItemId,
        [itemId]: AgentSetupPanelPhase.DONE,
      },
      draftByItemId: {
        ...state.draftByItemId,
        [itemId]: {
          ...current,
          templateId: template.templateId,
          name: template.name,
          doneSummary: null,
          caveats: [],
        },
      },
    };
  });
};

export const handleApplyTemplateSnapshot = (
  set: HandlerSet,
  itemId: string,
  template: CreatedAgentDraft,
) => {
  updateDraft(set, itemId, () => ({ template }));
};

export const handleSetDraftPatch = (
  set: HandlerSet,
  itemId: string,
  patch: Partial<Pick<AgentSetupDraftState, 'name' | 'job'>>,
) => {
  updateDraft(set, itemId, () => patch);
};

export const handleSetToolPolicies = (
  set: HandlerSet,
  itemId: string,
  toolPolicies: AgentToolPolicy[],
) => {
  updateDraft(set, itemId, () => ({
    toolPolicies: toToolPolicyInputs(toolPolicies),
  }));
};

export const handleSetToolSets = (
  set: HandlerSet,
  itemId: string,
  toolSets: string[],
) => {
  updateDraft(set, itemId, () => ({ toolSets }));
};

export const handleSetVisibility = (
  set: HandlerSet,
  itemId: string,
  visibility: AgentTemplateVisibility,
) => {
  updateDraft(set, itemId, () => ({ visibility }));
};

export const handleSetApprovalMode = (
  set: HandlerSet,
  itemId: string,
  approvalMode: AgentApprovalMode,
) => {
  updateDraft(set, itemId, () => ({ approvalMode }));
};

export const handleSetOutbound = (
  set: HandlerSet,
  itemId: string,
  outbound: AgentOutboundDraft,
) => {
  updateDraft(set, itemId, () => ({ outbound }));
};

export const handleSetDraftSaving = (
  set: HandlerSet,
  itemId: string,
  isSaving: boolean,
) => {
  updateDraft(set, itemId, () => ({ isSaving }));
};

export const handleSetDraftRegenerating = (
  set: HandlerSet,
  itemId: string,
  isRegenerating: boolean,
) => {
  updateDraft(set, itemId, () => ({ isRegenerating }));
};

export const handleSetDoneSummary = (
  set: HandlerSet,
  itemId: string,
  summary: AgentSetupTrialDoneSummary,
) => {
  updateDraft(set, itemId, () => ({ doneSummary: summary }));
};

export const handleSetDraftCaveats = (
  set: HandlerSet,
  itemId: string,
  caveats: string[],
) => {
  updateDraft(set, itemId, () => ({ caveats }));
};

export const handleClearSuggestedDestinations = (
  set: HandlerSet,
  itemId: string,
) => {
  updateDraft(set, itemId, () => ({ suggestedDestinations: [] }));
};

export const handleSetDraftSuggestions = (
  set: HandlerSet,
  itemId: string,
  suggestions: Pick<
    AgentSetupDraftState,
    'missingBrainReferences' | 'suggestedDestinations'
  >,
) => {
  updateDraft(set, itemId, () => suggestions);
};

export const handleSetDraftResultDelivery = (
  set: HandlerSet,
  itemId: string,
  resultDelivery: AgentSetupResultDelivery,
) => {
  updateDraft(set, itemId, () => ({ resultDelivery }));
};

const updateConversation = (
  set: HandlerSet,
  itemId: string,
  updater: (
    conversation: AgentSetupConversationState,
  ) => Partial<AgentSetupConversationState>,
) => {
  set((state) => {
    const current = state.conversationByItemId[itemId];
    if (!current) {
      return {};
    }
    return {
      conversationByItemId: {
        ...state.conversationByItemId,
        [itemId]: { ...current, ...updater(current) },
      },
    };
  });
};

const appendMessage = (
  conversation: AgentSetupConversationState,
  message: Omit<AgentSetupConversationMessage, 'id'>,
): Partial<AgentSetupConversationState> => ({
  messages: [
    ...conversation.messages,
    { ...message, id: `${message.role}-${conversation.messages.length}` },
  ],
});

export const handleAddConversationUserMessage = (
  set: HandlerSet,
  itemId: string,
  content: string,
) => {
  updateConversation(set, itemId, (conversation) =>
    appendMessage(conversation, {
      role: AgentSetupConversationRole.USER,
      content,
      suggestedReplies: [],
      missingCapabilities: [],
      isLocalNotice: false,
    }),
  );
};

export const handleAddConversationAssistantMessage = (
  set: HandlerSet,
  itemId: string,
  message: AgentSetupAssistantTurnMessage,
) => {
  updateConversation(set, itemId, (conversation) =>
    appendMessage(conversation, {
      role: AgentSetupConversationRole.ASSISTANT,
      content: message.content,
      suggestedReplies: message.suggestedReplies ?? [],
      missingCapabilities: message.missingCapabilities ?? [],
      isLocalNotice: message.isLocalNotice ?? false,
    }),
  );
};

export const handleSetConversationThinking = (
  set: HandlerSet,
  itemId: string,
  isThinking: boolean,
) => {
  updateConversation(set, itemId, () => ({ isThinking }));
};

export const handleClearConversationTranscript = (
  set: HandlerSet,
  itemId: string,
) => {
  updateConversation(set, itemId, () => ({ messages: [] }));
};
